
import { ImageLibraryClient } from './client';
import { toast } from '@/hooks/errors/use-toast';

/**
 * Check if an image is favorited by the current user
 */
export async function isImageFavorited(imageId: string): Promise<boolean> {
  const { data: { session } } = await ImageLibraryClient.supabase.auth.getSession();
  if (!session?.user) return false;

  try {
    const { data, error } = await ImageLibraryClient.supabase
      .from('image_favorites')
      .select('id')
      .eq('image_id', imageId)
      .eq('user_id', session.user.id)
      .single();

    if (error) {
      // Handle 406 RLS policy errors gracefully - return false instead of throwing
      if (error.code === 'PGRST301' || error.message?.includes('Not Acceptable')) {
        console.warn(`[Favorites] RLS policy blocking favorite check for image ${imageId}, defaulting to false`);
        return false;
      }
      
      // Handle "no rows returned" error (PGRST116) - means not favorited
      if (error.code !== 'PGRST116') {
        console.error('Error checking favorite status:', error);
        return false;
      }
    }

    return !!data;
  } catch (error) {
    // Catch any network/unexpected errors and default to false
    console.warn(`[Favorites] Failed to check favorite status for image ${imageId}:`, error);
    return false;
  }
}

/**
 * Optimized version for pagination - uses cached userId to avoid repeated session calls
 */
export async function isImageFavoritedWithUserId(imageId: string, userId: string): Promise<boolean> {
  if (!userId) return false;

  try {
    const { data, error } = await ImageLibraryClient.supabase
      .from('image_favorites')
      .select('id')
      .eq('image_id', imageId)
      .eq('user_id', userId)
      .single();

    if (error) {
      // Handle 406 RLS policy errors gracefully - return false instead of throwing
      if (error.code === 'PGRST301' || error.message?.includes('Not Acceptable')) {
        console.warn(`[Favorites] RLS policy blocking favorite check for image ${imageId}, defaulting to false`);
        return false;
      }
      
      // Handle "no rows returned" error (PGRST116) - means not favorited
      if (error.code !== 'PGRST116') {
        console.error('Error checking favorite status:', error);
        return false;
      }
    }

    return !!data;
  } catch (error) {
    // Catch any network/unexpected errors and default to false
    console.warn(`[Favorites] Failed to check favorite status for image ${imageId}:`, error);
    return false;
  }
}

/**
 * Toggle favorite status for an image
 */
export async function toggleFavoriteImage(imageId: string): Promise<boolean> {
  console.log('🎯 [Favorites] Starting toggle for image:', imageId);
  
  try {
    const { data: { session } } = await ImageLibraryClient.supabase.auth.getSession();
    if (!session?.user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to favorite images",
        variant: "destructive"
      });
      return false;
    }

    const userId = session.user.id;
    console.log('🎯 [Favorites] User ID:', userId);

    // Check current favorite status
    const { data: existingFavorite, error: checkError } = await ImageLibraryClient.supabase
      .from('image_favorites')
      .select('id')
      .eq('image_id', imageId)
      .eq('user_id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('🎯 [Favorites] Error checking existing favorite:', checkError);
      toast({
        title: "Error checking favorite status",
        description: checkError.message,
        variant: "destructive"
      });
      return false;
    }

    const isFavorited = !!existingFavorite;
    console.log('🎯 [Favorites] Current status:', isFavorited ? 'favorited' : 'not favorited');

    if (isFavorited) {
      // Remove favorite
      const { error } = await ImageLibraryClient.supabase
        .from('image_favorites')
        .delete()
        .eq('image_id', imageId)
        .eq('user_id', userId);

      if (error) {
        console.error('🎯 [Favorites] Error removing favorite:', error);
        toast({
          title: "Error removing favorite",
          description: error.message,
          variant: "destructive"
        });
        return false;
      }

      console.log('🎯 [Favorites] Successfully removed favorite');
      toast({
        title: "Removed from favorites",
        description: "Image removed from your favorites",
        variant: "default"
      });
      return false;
    } else {
      // Add favorite
      const { error } = await ImageLibraryClient.supabase
        .from('image_favorites')
        .insert([{
          image_id: imageId,
          user_id: userId
        }]);

      if (error) {
        console.error('🎯 [Favorites] Error adding favorite:', error);
        toast({
          title: "Error adding favorite",
          description: error.message,
          variant: "destructive"
        });
        return false;
      }

      console.log('🎯 [Favorites] Successfully added favorite');
      toast({
        title: "Added to favorites",
        description: "Image added to your favorites",
        variant: "default"
      });
      return true;
    }
  } catch (error) {
    console.error('🎯 [Favorites] Unexpected error:', error);
    toast({
      title: "Unexpected error",
      description: "An unexpected error occurred",
      variant: "destructive"
    });
    return false;
  }
}
